import Box, { type BoxProps } from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { fonts, radius } from '@/shared/theme';
import { Icon, type IconName } from './icon';
import { IconChip } from './indicators';

/** Kicker + large title above a block of content ("NACH STANDORT / Kennzahlen je Standort"). */
export function SectionHeading({
  kicker,
  title,
  description,
  aside,
  size = 'lg',
  id,
}: {
  kicker?: string;
  title: string;
  /** Explanatory paragraph under the title (form sections). */
  description?: ReactNode;
  aside?: ReactNode;
  size?: 'lg' | 'md';
  id?: string;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 2,
        mb: '14px',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        {kicker && (
          <Typography variant="kicker" component="div" sx={{ fontSize: size === 'lg' ? 11 : 10.5 }}>
            {kicker}
          </Typography>
        )}
        <Typography
          id={id}
          component="h2"
          sx={{
            fontFamily: fonts.head,
            fontWeight: 800,
            fontSize: size === 'lg' ? { xs: 19, sm: 23 } : 18,
            letterSpacing: size === 'lg' ? '-.02em' : '-.01em',
            mt: size === 'lg' ? '5px' : '3px',
          }}
        >
          {title}
        </Typography>
        {description && (
          <Box
            sx={{
              mt: '6px',
              maxWidth: 620,
              fontSize: 12.5,
              lineHeight: 1.5,
              color: 'text.secondary',
              textWrap: 'pretty',
            }}
          >
            {description}
          </Box>
        )}
      </Box>
      {aside && <Box sx={{ minWidth: 0 }}>{aside}</Box>}
    </Box>
  );
}

interface PanelProps extends BoxProps {
  /** Slightly stronger shadow, for cards that should stand off the page (dashboard). */
  raised?: boolean;
}

/** Panel surface ("md-card" without hover lift). */
export function Panel({ raised = false, sx, children, ...props }: PanelProps) {
  return (
    <Box
      sx={[
        (theme) => ({
          background: theme.vars.palette.md.panel,
          border: `1px solid ${theme.vars.palette.md.border}`,
          borderRadius: radius.card,
          boxShadow: raised ? theme.vars.palette.md.shadowRaised : theme.vars.palette.md.shadow,
          minWidth: 0,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...props}
    >
      {children}
    </Box>
  );
}

/** Panel header row: optional icon, kicker, title and trailing content (count, hint, actions). */
export function PanelHeader({
  icon,
  kicker,
  title,
  aside,
}: {
  icon?: IconName;
  kicker?: string;
  title: string;
  aside?: ReactNode;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', p: '16px 18px 13px' }}>
      {icon && (
        <Box
          sx={(theme) => ({ display: 'flex', flex: 'none', color: theme.vars.palette.md.primary })}
        >
          <Icon name={icon} />
        </Box>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {kicker && (
          <Typography variant="kicker" component="div">
            {kicker}
          </Typography>
        )}
        <Typography variant="h3" component="h3" sx={{ mt: kicker ? '3px' : 0 }}>
          {title}
        </Typography>
      </Box>
      {aside && (
        <Box sx={{ flex: 'none', fontSize: 11.5, color: 'text.secondary', fontWeight: 700 }}>
          {aside}
        </Box>
      )}
    </Box>
  );
}

interface StatCardProps {
  icon: IconName;
  hue: string;
  label: string;
  value: ReactNode;
  /** Secondary value next to the main figure, e.g. "(8,6 %)". */
  sub?: ReactNode;
  delta?: ReactNode;
  footer?: ReactNode;
  loading?: boolean;
  /**
   * `labelled`: label beside the icon, figure below and a faint note (revenue / utilization
   * cards). `sub` is then the note under the figure.
   */
  variant?: 'default' | 'labelled';
}

/** KPI card with icon chip, change pill, large figure and label (appointment/users/… screens). */
export function StatCard({
  icon,
  hue,
  label,
  value,
  sub,
  delta,
  footer,
  loading,
  variant = 'default',
}: StatCardProps) {
  if (variant === 'labelled') {
    return (
      <Panel sx={{ p: '18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {/* The label drops below the chip rather than being clipped in a narrow two-up card. */}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '11px' }}>
          <IconChip icon={icon} hue={hue} size={40} />
          <Box
            component="span"
            sx={{
              fontSize: 12.5,
              color: 'text.secondary',
              fontWeight: 700,
              lineHeight: 1.3,
              minWidth: 0,
              overflowWrap: 'anywhere',
            }}
          >
            {label}
          </Box>
          {!loading && delta && <Box sx={{ ml: 'auto' }}>{delta}</Box>}
        </Box>
        <Box
          sx={{
            fontFamily: fonts.head,
            fontWeight: 800,
            fontSize: { xs: 22, sm: 27 },
            lineHeight: 1.1,
            letterSpacing: '-.02em',
            fontVariantNumeric: 'tabular-nums',
            overflowWrap: 'anywhere',
          }}
        >
          {loading ? <Skeleton width={120} height={30} /> : value}
        </Box>
        {!loading && sub && (
          <Box
            sx={(theme) => ({
              fontSize: 11.5,
              color: theme.vars.palette.md.faint,
              fontWeight: 600,
            })}
          >
            {sub}
          </Box>
        )}
        {footer}
      </Panel>
    );
  }
  return (
    <Panel
      sx={(theme) => ({
        p: '18px',
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
        transition: 'transform .16s ease, box-shadow .16s ease',
        '&:hover': { transform: 'translateY(-3px)', boxShadow: theme.vars.palette.md.shadowLg },
      })}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25 }}
      >
        <IconChip icon={icon} hue={hue} />
        {!loading && delta}
      </Box>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <Box
            sx={{
              fontFamily: fonts.head,
              fontWeight: 800,
              fontSize: { xs: 23, sm: 29 },
              lineHeight: 1,
              letterSpacing: '-.02em',
              fontVariantNumeric: 'tabular-nums',
              overflowWrap: 'anywhere',
              minWidth: 0,
            }}
          >
            {loading ? <Skeleton width={110} height={29} /> : value}
          </Box>
          {!loading && sub && (
            <Box sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 600 }}>{sub}</Box>
          )}
        </Box>
        <Box sx={{ mt: 1, fontSize: 12.5, color: 'text.secondary', fontWeight: 600 }}>{label}</Box>
      </Box>
      {footer}
    </Panel>
  );
}

/**
 * Responsive grid for stat cards: auto-fit columns, two columns below 1180px (".md-kpigrid").
 */
export function StatGrid({ children, min = 196 }: { children: ReactNode; min?: number }) {
  return (
    <Box
      sx={(theme) => ({
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
        gap: 2,
        mb: '28px',
        [theme.breakpoints.down('xl')]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
        // Two up on a phone as well: stacked full-width cards pushed the real content of the page
        // several screens down. The value type steps down to match the narrower card.
        [theme.breakpoints.down('sm')]: { gap: 1.5 },
      })}
    >
      {children}
    </Box>
  );
}

/** Inline loading notice ("Daten werden geladen …"). */
export function LoadingNotice({ label }: { label: string }) {
  return (
    <Panel
      role="status"
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '11px',
        p: '12px 16px',
        mb: '18px',
        borderRadius: radius.card,
        boxShadow: theme.vars.palette.md.shadowSm,
        fontSize: 13,
        fontWeight: 600,
        color: 'text.secondary',
      })}
    >
      <Box sx={(theme) => ({ display: 'flex', color: theme.vars.palette.md.primary })}>
        <Icon name="loader" spin />
      </Box>
      {label}
    </Panel>
  );
}
